/** @format */

import React, { useState, useEffect } from 'react';
import TextInput from '../../../../components/ui/TextInput';
import ImageUpload from '../../../../components/ui/ImageUpload';
import TextAreaInput from '../../../../components/ui/TextAreaInput';
import TextEditor from '../../../../components/ui/TextEditor';
import { AuthButton } from '../../../../components/common/AuthButton';

import axios from 'axios';
import { useAuthStore } from '../../../../context/useAuthStore';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const EditBlogPostPage: React.FC = () => {
	const { slug: paramsSlug } = useParams<{ slug: string }>();
	const navigate = useNavigate();
	const { token } = useAuthStore();
	const [title, setTitle] = useState('');
	const [slug, setSlug] = useState('');
	const [blogId, setBlogId] = useState<string | null>(null);

	const [image, setImage] = useState<File | null>(null);
	const [existingImage, setExistingImage] = useState<string>('');
	const [shortDescription, setShortDescription] = useState('');
	const [content, setContent] = useState('');
	const [loading, setLoading] = useState(false);
	const [fetching, setFetching] = useState(false);

	useEffect(() => {
		const fetchBlog = async () => {
			setFetching(true);
			try {
				const searchParams = new URLSearchParams(window.location.search);
				const queryId = searchParams.get('blogId');

				if (!queryId && !paramsSlug) {
					setFetching(false);
					return;
				}

				const response = await axios.get(`${API_BASE_URL}/blog`, {
					params: queryId ? { blogId: queryId } : { slug: paramsSlug },
					headers: { Authorization: token ? `Bearer ${token}` : '' },
				});

				let blog = response.data.success || response.data;

				// Handle case where API returns an array (single item)
				if (Array.isArray(blog)) {
					blog = blog[0];
				}

				console.log('Fetched Blog Data:', blog);

				if (blog) {
					setBlogId(blog._id || blog.blogId);
					setTitle(blog.title);
					setSlug(blog.slug);
					setShortDescription(
						blog.shortDescription || blog.excerpt || blog.description || ''
					);
					let fixedContent = blog.content || '';
					// Fix missing www in image URLs
					fixedContent = fixedContent.replace(
						/https?:\/\/(?:www\.)?llb360\.com\/uploads\/blogs\//g,
						'https://www.llb360.com/uploads/blogs/'
					);
					setContent(fixedContent);
					setExistingImage(
						(() => {
							if (!blog.image) return '';
							const filename = blog.image.split('/').pop();
							return `https://www.llb360.com/uploads/blogs/${filename}`;
						})()
					);
				}
			} catch (error) {
				console.error('Error fetching blog:', error);
				toast.error('Failed to fetch blog details');
				navigate('/admin/all-blog-posts');
			} finally {
				setFetching(false);
			}
		};

		if (paramsSlug || window.location.search.includes('blogId')) {
			fetchBlog();
		}
	}, [paramsSlug, token, navigate]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (
			!title ||
			!slug ||
			(!image && !existingImage) ||
			!shortDescription ||
			!content
		) {
			toast.error('Please fill in all fields');
			return;
		}

		setLoading(true);

		try {
			// 1. Upload New Image First (if selected)
			let imageUrl = '';
			if (image) {
				const imageFormData = new FormData();
				imageFormData.append('image', image);

				const uploadResponse = await axios.post(
					`${API_BASE_URL}/imageupload`,
					imageFormData,
					{
						headers: {
							'Content-Type': 'multipart/form-data',
							Authorization: token ? `Bearer ${token}` : '',
						},
					}
				);

				const data = uploadResponse.data;
				if (data && data.success) {
					imageUrl = data.data?.imageUrl || data.imageUrl || data.url;
					if (!imageUrl) {
						throw new Error('Image URL missing in upload response');
					}
				} else {
					throw new Error('Image upload failed');
				}
			}

			// 2. Update Blog Post with Image URL
			const blogData = {
				title,
				slug,
				shortDescription,
				excerpt: shortDescription,
				content,
				image: imageUrl || existingImage,
			};

			if (blogId) {
				await axios.put(
					`${API_BASE_URL}/blog`,
					{ ...blogData, blogId },
					{
						headers: {
							'Content-Type': 'application/json',
							Authorization: token ? `Bearer ${token}` : '',
						},
					}
				);
				toast.success('Blog post updated successfully!');
				navigate('/admin/all-blog-posts');
			} else {
				toast.error('Blog ID missing for update');
			}
		} catch (error: any) {
			console.error('Error updating blog post:', error);
			const message =
				error.response?.data?.message || 'Failed to update blog post';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	if (fetching) return <p className='p-4'>Loading...</p>;

	return (
		<section>
			<h1 className='text-2xl font-bold'>Edit Blog Post</h1>
			<form
				className='space-y-4 p-4'
				onSubmit={handleSubmit}>
				<TextInput
					id='title'
					name='title'
					label='Title'
					type='text'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder='Enter title'
					required
					errorMessage={'This field is required'}
				/>
				<p className='text-xs text-myDark pl-4'>
					<b>Slug:</b>{' '}
					<span className='text-myDark font-semibold lowercase'>{slug}</span>
				</p>
				<div className='grid md:grid-cols-2 grid-cols-1 gap-4'>
					<ImageUpload
						id='featureImage'
						name='featureImage'
						label='Feature Image'
						value={image || existingImage}
						onChange={setImage}
						required={!existingImage && !image}
						errorMessage='Please upload a feature image'
					/>
					<TextAreaInput
						id='shortDescription'
						name='shortDescription'
						label='Short Description'
						value={shortDescription}
						onChange={(e) => setShortDescription(e.target.value)}
						required
						errorMessage='This field is required'
					/>
				</div>
				<TextEditor
					label='Content'
					value={content}
					onChange={setContent}
					error={false} // Add validation logic if needed
					errorMessage='Content is required'
				/>
				<AuthButton
					title='Update Blog Post'
					loadingTitle='Updating...'
					loading={loading}
					disabled={
						loading ||
						!title ||
						(!image && !existingImage) ||
						!shortDescription ||
						!content
					}
				/>
			</form>
		</section>
	);
};

export default EditBlogPostPage;
